import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, clearToken, clearSystemUnlock } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import {
    ShoppingCart,
    Users,
    Package,
    ChartBar,
    FloppyDisk,
    SignOut,
    UserCircle,
    Warning,
    Sliders,
} from "@phosphor-icons/react";

const ROLE_LABELS = {
    admin: "ΔΙΑΧΕΙΡΙΣΤΗΣ",
    manager: "ΥΠΕΥΘΥΝΟΣ",
    user: "ΧΡΗΣΤΗΣ",
};

const ROLE_COLOR = {
    admin: "#FF3B30",
    manager: "#5856D6",
    user: "#34C759",
};

export default function Layout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, canAccessSecurity } = useAuth();

    // Fetch license status — show subtle banner when urgent or expired.
    // Refresh every 10 minutes to catch the daily decrement.
    const [license, setLicense] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const load = () =>
            api
                .get("/license/public")
                .then((r) => {
                    if (!cancelled) setLicense(r.data || null);
                })
                .catch(() => {});
        load();
        const id = setInterval(load, 10 * 60 * 1000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    // Auto full-logout at 00:00 Athens local time. Clears both token and
    // system-unlock flag so the next operator must do Step 1 again.
    useEffect(() => {
        let timeoutId = null;
        const scheduleNext = () => {
            const now = new Date();
            const next = new Date(now);
            next.setHours(24, 0, 5, 0); // 00:00:05 next day (5s buffer)
            const ms = next.getTime() - now.getTime();
            timeoutId = setTimeout(() => {
                try {
                    clearToken();
                    clearSystemUnlock();
                } finally {
                    // Hard reload ensures all in-memory state is wiped.
                    window.location.href = "/login";
                }
            }, ms);
        };
        scheduleNext();
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, []);

    const NAV_ITEMS = [
        {
            to: "/sale",
            label: "ΠΩΛΗΣΗ",
            icon: ShoppingCart,
            color: "#FF3B30",
            testId: "nav-sale",
            aliases: ["/", "/dashboard"],
        },
        {
            to: "/customers",
            label: "ΠΕΛΑΤΕΣ",
            icon: Users,
            color: "#002FA7",
            testId: "nav-customers",
        },
        {
            to: "/products",
            label: "ΕΙΔΗ",
            icon: Package,
            color: "#FF9500",
            testId: "nav-products",
        },
        {
            to: "/reports",
            label: "ΣΤΑΤΙΣΤΙΚΑ",
            icon: ChartBar,
            color: "#34C759",
            testId: "nav-reports",
        },
        ...(canAccessSecurity
            ? [
                  {
                      to: "/backup",
                      label: "ΑΣΦΑΛΕΙΑ",
                      icon: FloppyDisk,
                      color: "#5856D6",
                      testId: "nav-backup",
                  },
              ]
            : []),
        {
            to: "/settings",
            label: "ΡΥΘΜΙΣΕΙΣ",
            icon: Sliders,
            color: "#0A0A0A",
            testId: "nav-settings",
        },
    ];

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    const isActive = (item) => {
        if (location.pathname === item.to) return true;
        if (item.aliases?.includes(location.pathname)) return true;
        return false;
    };

    const NavButton = ({ item }) => {
        const Icon = item.icon;
        const active = isActive(item);
        return (
            <Link
                to={item.to}
                data-testid={item.testId}
                className={`relative flex flex-col items-center justify-center gap-1.5 transition-all duration-150 active:scale-[0.97] flex-1 min-h-0 border-2 ${
                    active
                        ? "text-white shadow-md"
                        : "bg-white text-[#0A0A0A] hover:brightness-95"
                }`}
                style={{
                    backgroundColor: active ? item.color : "white",
                    borderColor: item.color,
                }}
            >
                <Icon size={28} weight={active ? "fill" : "bold"} />
                <span className="text-xs font-black uppercase tracking-wider text-center leading-tight px-1">
                    {item.label}
                </span>
                {active && (
                    <div className="absolute right-0 top-0 bottom-0 w-1 bg-white" />
                )}
            </Link>
        );
    };

    const roleKey = user?.is_supervisor ? "admin" : user?.role || "user";
    const roleLabel = ROLE_LABELS[roleKey] || "ΧΡΗΣΤΗΣ";
    const roleColor = ROLE_COLOR[roleKey] || "#34C759";

    return (
        <div className="h-screen flex bg-white overflow-hidden" data-testid="app-layout">
            {/* Sidebar */}
            <aside className="hidden md:flex w-[120px] flex-col border-r-2 border-[#0A0A0A] bg-white">
                {/* Brand strip */}
                <div className="h-12 bg-[#0A0A0A] flex items-center justify-center px-1">
                    <div
                        className="font-heading font-black text-[10px] tracking-tight text-white text-center leading-none"
                        data-testid="brand-tag"
                        title="MyCreditManager.gr"
                    >
                        MyCredit
                        <br />
                        Manager
                        <span className="text-[#5DA0FF]">.gr</span>
                    </div>
                </div>

                {/* Nav buttons - flex grow so they fill vertical space */}
                <nav className="flex-1 flex flex-col gap-1.5 p-1.5 min-h-0">
                    {NAV_ITEMS.map((item) => (
                        <NavButton key={item.to} item={item} />
                    ))}
                </nav>

                {/* User info + Logout */}
                <div className="border-t-2 border-[#0A0A0A]">
                    <div
                        className="px-1.5 pt-1.5 pb-1 flex flex-col items-center gap-0.5"
                        data-testid="current-user-info"
                    >
                        <UserCircle size={20} weight="fill" className="text-[#0A0A0A]" />
                        <div
                            className="text-[10px] font-black uppercase tracking-wider text-[#0A0A0A] text-center leading-tight break-all"
                            data-testid="current-user-name"
                            title={user?.name || ""}
                        >
                            {(user?.name || "—").slice(0, 18)}
                        </div>
                        <div
                            className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 text-white w-full text-center"
                            style={{ backgroundColor: roleColor }}
                            data-testid="current-user-role"
                        >
                            {roleLabel}
                        </div>
                    </div>
                    <div className="p-1.5">
                        <button
                            onClick={handleLogout}
                            data-testid="logout-button"
                            className="w-full h-10 flex items-center justify-center gap-1.5 bg-[#0A0A0A] text-white hover:bg-[#1F1F1F] active:scale-[0.97] transition-all text-[10px] font-black tracking-wider"
                        >
                            <SignOut size={14} weight="bold" />
                            ΕΞΟΔΟΣ
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-2 h-12 bg-[#0A0A0A] text-white">
                <span
                    className="font-heading font-black tracking-tight text-xs"
                    data-testid="brand-tag-mobile"
                >
                    MyCreditManager<span className="text-[#5DA0FF]">.gr</span>
                </span>
                <div className="flex items-center gap-1 overflow-x-auto">
                    {NAV_ITEMS.map((item) => (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={`px-2 py-1 text-[10px] font-bold whitespace-nowrap ${
                                isActive(item) ? "text-white underline" : "text-white/70"
                            }`}
                            style={{
                                backgroundColor: isActive(item) ? item.color : "transparent",
                            }}
                        >
                            {item.label}
                        </Link>
                    ))}
                    <span
                        className="px-1.5 py-0.5 text-[9px] font-black tracking-wider"
                        style={{ backgroundColor: roleColor }}
                        data-testid="current-user-role-mobile"
                    >
                        {(user?.name || "—").slice(0, 10)}
                    </span>
                    <button
                        onClick={handleLogout}
                        data-testid="logout-button-mobile"
                        className="p-1 text-white/80"
                    >
                        <SignOut size={14} />
                    </button>
                </div>
            </div>

            {/* Main — full screen, no scroll */}
            <main className="flex-1 min-w-0 pt-12 md:pt-0 h-screen overflow-hidden flex flex-col">
                <LicenseAlertBar license={license} />
                {children}
            </main>
        </div>
    );
}

function LicenseAlertBar({ license }) {
    if (!license || !license.is_set) return null;
    const sev = license.severity;
    // Only show for urgent (<10) or expired (<=0) — keep it subtle
    if (sev !== "urgent" && sev !== "expired") return null;
    const days = Math.max(0, license.days_remaining);
    const isExpired = sev === "expired";
    return (
        <div
            className={`flex items-center gap-2 px-3 md:px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] border-b-2 animate-pulse-slow ${
                isExpired
                    ? "bg-[#0A0A0A] text-white border-[#FF3B30]"
                    : "bg-[#FF3B30] text-white border-[#A4291F]"
            }`}
            data-testid="license-alert-bar"
            data-severity={sev}
        >
            <Warning size={14} weight="fill" className="shrink-0" />
            <span className="truncate">
                {isExpired
                    ? "Η ΑΔΕΙΑ ΛΕΙΤΟΥΡΓΙΑΣ ΤΟΥ ΠΡΟΓΡΑΜΜΑΤΟΣ ΕΧΕΙ ΛΗΞΕΙ"
                    : `ΑΠΟΜΕΝΟΥΝ ${days} ΗΜΕΡΕΣ — ΑΝΑΝΕΩΣΤΕ ΑΜΕΣΑ ΤΗΝ ΑΔΕΙΑ`}
            </span>
        </div>
    );
}
